import React, { useEffect, useState, useMemo } from "react";
import { Card, Table, Typography, Spin, Row, Col, Input, Select, Space, Switch, Button, message } from "antd";
import { SearchOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useReportApi } from "../../store/reportContext";
import RestoreStockButton from "../../components/Product/RestoreStockButton";
import ExcelExportButton from "../../components/Report/ExcelExportButton";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function formatNumber(num: number | undefined) {
    if (typeof num !== 'number') return num;
    return num.toLocaleString();
}

const { Title } = Typography;

interface UnitDetail {
    initial: number;
    bought: number;
    sold: number;
    returned: number;
    final: number;
    valueIQD: number;
    valueUSD: number;
}

interface Product {
    id: string;
    code: string;
    name: string;
    units: Record<string, UnitDetail>;
    totalIQD: number;
    totalUSD: number;
}

interface ReportData {
    products: Product[];
    grandTotalIQD: number;
    grandTotalUSD: number;
}

const ProductStockValueReportPage: React.FC = () => {
    const [data, setData] = useState<ReportData | null>(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [filterUnit, setFilterUnit] = useState<string | undefined>(undefined);
    const [onlyInStock, setOnlyInStock] = useState(false);
    const { fetchStockValueReport } = useReportApi();
    const { t } = useTranslation();

    const fetchData = async () => {
        setLoading(true);
        try {
            const result = await fetchStockValueReport();
            setData(result);
        } catch (error) {
            message.error(t("failed_to_fetch_data") || "Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = () => {
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor: [number, number, number] = [30, 41, 59];
        const accentColor: [number, number, number] = [59, 130, 246];
        const lightGray: [number, number, number] = [248, 250, 252];
        const borderGray: [number, number, number] = [226, 232, 240];
        const white: [number, number, number] = [255, 255, 255];
        const today = new Date().toLocaleDateString("en-GB");

        // Header background
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 28, "F");

        // Title
        doc.setTextColor(...white);
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text("Product Stock Value Report", 14, 13);

        // Subtitle / date
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`Generated: ${today}`, 14, 21);

        // Summary pills (top right)
        const summaryX = pageWidth - 14;
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text(`Grand Total IQD: ${formatNumber(filteredGrandTotalIQD)}`, summaryX, 12, { align: "right" });
        doc.text(`Grand Total USD: ${formatNumber(filteredGrandTotalUSD)}`, summaryX, 20, { align: "right" });

        let currentY = 35;

        filteredProducts.forEach((product, idx) => {
            const unitRows = Object.entries(product.units).map(([unit, d]) => [
                unit.charAt(0).toUpperCase() + unit.slice(1),
                formatNumber(d.initial) ?? 0,
                formatNumber(d.bought) ?? 0,
                formatNumber(d.sold) ?? 0,
                formatNumber(d.returned) ?? 0,
                formatNumber(d.final) ?? 0,
                formatNumber(d.valueIQD) ?? 0,
                formatNumber(d.valueUSD) ?? 0,
            ]);

            // Estimate required height: product header (8mm) + rows (7mm each) + spacing (6mm)
            const estimatedHeight = 8 + unitRows.length * 7 + 6;
            if (currentY + estimatedHeight > pageHeight - 20) {
                doc.addPage();
                // Re-draw header on new page
                doc.setFillColor(...primaryColor);
                doc.rect(0, 0, pageWidth, 14, "F");
                doc.setTextColor(...white);
                doc.setFontSize(10);
                doc.setFont("helvetica", "bold");
                doc.text("Product Stock Value Report (cont.)", 14, 9);
                currentY = 20;
            }

            // Product card background
            doc.setFillColor(...lightGray);
            doc.setDrawColor(...borderGray);
            doc.roundedRect(10, currentY - 2, pageWidth - 20, estimatedHeight, 2, 2, "FD");

            // Product index badge
            doc.setFillColor(...accentColor);
            doc.roundedRect(13, currentY, 8, 6, 1, 1, "F");
            doc.setTextColor(...white);
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text(String(idx + 1), 17, currentY + 4.5, { align: "center" });

            // Product code & name
            doc.setTextColor(...primaryColor);
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.text(`${product.code}  —  ${product.name}`, 25, currentY + 4.5);

            // Totals on the right
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(71, 85, 105);
            doc.text(
                `Total IQD: ${formatNumber(product.totalIQD)}   |   Total USD: ${formatNumber(product.totalUSD)}`,
                pageWidth - 14,
                currentY + 4.5,
                { align: "right" }
            );

            currentY += 9;

            autoTable(doc, {
                startY: currentY,
                margin: { left: 13, right: 13 },
                head: [["Unit", "Initial", "Bought", "Sold", "Returned", "Final", "Value (IQD)", "Value ($)"]],
                body: unitRows,
                theme: "grid",
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    valign: "middle",
                    textColor: [30, 41, 59],
                    lineColor: borderGray,
                    lineWidth: 0.2,
                },
                headStyles: {
                    fillColor: primaryColor,
                    textColor: white,
                    fontStyle: "bold",
                    fontSize: 8,
                },
                alternateRowStyles: { fillColor: [241, 245, 249] },
                columnStyles: {
                    0: { fontStyle: "bold", cellWidth: 28 },
                    1: { halign: "right" },
                    2: { halign: "right" },
                    3: { halign: "right" },
                    4: { halign: "right" },
                    5: { halign: "right", fontStyle: "bold" },
                    6: { halign: "right" },
                    7: { halign: "right" },
                },
                didDrawPage: () => {},
            });

            currentY = (doc as any).lastAutoTable.finalY + 6;
        });

        // Grand Total footer bar
        if (currentY + 14 > pageHeight - 10) {
            doc.addPage();
            currentY = 20;
        }
        doc.setFillColor(...primaryColor);
        doc.roundedRect(10, currentY, pageWidth - 20, 12, 2, 2, "F");
        doc.setTextColor(...white);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("GRAND TOTAL", 18, currentY + 8);
        doc.text(
            `IQD: ${formatNumber(filteredGrandTotalIQD)}     USD: ${formatNumber(filteredGrandTotalUSD)}`,
            pageWidth - 18,
            currentY + 8,
            { align: "right" }
        );

        // Page numbers
        const totalPages = (doc as any).internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFontSize(7);
            doc.setTextColor(148, 163, 184);
            doc.setFont("helvetica", "normal");
            doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 5, { align: "center" });
        }

        doc.save(`Product_Stock_Value_Report_${today.replace(/\//g, "-")}.pdf`);
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Collect all unique unit keys across products
    const allUnits = useMemo(() => {
        if (!data) return [];
        const unitSet = new Set<string>();
        data.products.forEach(p => Object.keys(p.units).forEach(u => unitSet.add(u)));
        return Array.from(unitSet);
    }, [data]);

    const filteredProducts = useMemo(() => {
        if (!data) return [];
        const q = search.toLowerCase();
        return data.products.filter(p => {
            const matchesSearch = !q || p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q);
            const matchesUnit = !filterUnit || Object.keys(p.units).includes(filterUnit);
            const matchesStock = !onlyInStock || Object.values(p.units).some(u => u.final > 0);
            return matchesSearch && matchesUnit && matchesStock;
        });
    }, [data, search, filterUnit, onlyInStock]);

    const filteredGrandTotalIQD = useMemo(() => filteredProducts.reduce((s, p) => s + (p.totalIQD || 0), 0), [filteredProducts]);
    const filteredGrandTotalUSD = useMemo(() => filteredProducts.reduce((s, p) => s + (p.totalUSD || 0), 0), [filteredProducts]);

    // The screen nests unit rows inside product rows; a spreadsheet cannot, so
    // the export flattens to one row per product/unit.
    const excelRows = useMemo(
        () =>
            filteredProducts.flatMap(product =>
                Object.entries(product.units).map(([unit, detail]) => ({
                    code: product.code,
                    name: product.name,
                    unit,
                    ...detail,
                    totalIQD: product.totalIQD,
                    totalUSD: product.totalUSD,
                }))
            ),
        [filteredProducts]
    );

    const excelColumns = [
        { header: t("product_code") as string, key: "code" as const, width: 16 },
        { header: t("product_name") as string, key: "name" as const, width: 28 },
        { header: t("unit") as string, key: "unit" as const, width: 12 },
        { header: (t("opening_stock") as string) || "Initial", key: "initial" as const, width: 12 },
        { header: t("purchases") as string, key: "bought" as const, width: 12 },
        { header: t("sales") as string, key: "sold" as const, width: 12 },
        { header: t("returns") as string, key: "returned" as const, width: 12 },
        { header: (t("current_stock") as string) || "Final", key: "final" as const, width: 14 },
        { header: `${t("value")} IQD`, key: "valueIQD" as const, width: 16 },
        { header: `${t("value")} USD`, key: "valueUSD" as const, width: 16 },
    ];

    const columns = [
        { title: "#", dataIndex: "index", key: "index", render: (_: any, __: any, idx: number) => idx + 1 },
        { title: t("code"), dataIndex: "code", key: "code" },
        { title: t("name"), dataIndex: "name", key: "name" },
        {
            title: t("unit"),
            dataIndex: "units",
            key: "units",
            render: (units: Product["units"]) => (
                <div style={{ minWidth: 600 }}>
                    <Table
                        size="small"
                        bordered
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        columns={[
                            { title: t("unit"), dataIndex: "unit", key: "unit" },
                            { title: t("initial"), dataIndex: "initial", key: "initial", render: formatNumber },
                            { title: t("bought"), dataIndex: "bought", key: "bought", render: formatNumber },
                            { title: t("sold"), dataIndex: "sold", key: "sold", render: formatNumber },
                            { title: t("returned"), dataIndex: "returned", key: "returned", render: formatNumber },
                            { title: t("final"), dataIndex: "final", key: "final", render: formatNumber },
                            { title: t("value_iqd"), dataIndex: "valueIQD", key: "valueIQD", render: formatNumber },
                            { title: t("value_usd"), dataIndex: "valueUSD", key: "valueUSD", render: formatNumber },
                        ]}
                        dataSource={Object.entries(units).map(([unit, detail]) => ({
                            unit: t(`unit.${unit}`),
                            ...detail,
                            key: unit
                        }))}
                    />
                </div>
            )
        },
        { title: `${t("totalAmount")} IQD`, dataIndex: "totalIQD", key: "totalIQD", render: (v: number) => formatNumber(v) },
        { title: `${t("totalAmount")} USD`, dataIndex: "totalUSD", key: "totalUSD", render: (v: number) => formatNumber(v) },
    ];

    return (
        <Card style={{ margin: 24 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Title level={3} style={{ margin: 0 }}>{t("product_stock_value_report")}</Title>
                </Col>
                <Col>
                    <Space>
                        <Button
                            icon={<FilePdfOutlined />}
                            onClick={handleDownloadPDF}
                            disabled={loading || !data || filteredProducts.length === 0}
                            style={{
                                background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
                                borderColor: "#b91c1c",
                                color: "#fff",
                                fontWeight: 600,
                                boxShadow: "0 2px 8px rgba(239,68,68,0.35)",
                            }}
                        >
                            {t("download_pdf") || "Download PDF"}
                        </Button>
                        <ExcelExportButton
                            fileName="product-stock-value"
                            sheetName={t("product_stock_value_report") as string}
                            columns={excelColumns}
                            rows={excelRows}
                            footer={[[
                                t("grand_total") as string, "", "", "", "", "", "", "",
                                filteredGrandTotalIQD,
                                filteredGrandTotalUSD,
                            ]]}
                            title={[t("product_stock_value_report") as string]}
                        />
                        <RestoreStockButton onSuccess={fetchData} />
                    </Space>
                </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle">
                <Col xs={24} md={8}>
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder={t("search_by_name_or_code") || "Search by name or code"}
                        allowClear
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </Col>
                <Col xs={24} md={6}>
                    <Select
                        allowClear
                        placeholder={t("filter_by_unit") || "Filter by unit"}
                        style={{ width: "100%" }}
                        value={filterUnit}
                        onChange={val => setFilterUnit(val)}
                        options={allUnits.map(u => ({ value: u, label: t(`unit.${u}`) || u }))}
                    />
                </Col>
                <Col xs={24} md={6}>
                    <Space>
                        <Switch checked={onlyInStock} onChange={setOnlyInStock} />
                        <span>{t("only_in_stock") || "Only in stock"}</span>
                    </Space>
                </Col>
            </Row>
            {loading ? <Spin /> : (
                <>
                    <Table
                        dataSource={filteredProducts.map((p, idx) => ({ ...p, key: p.id, index: idx + 1 }))}
                        columns={columns}
                        pagination={false}
                        bordered
                        scroll={{ x: 'max-content' }}
                        summary={() => (
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={4}><b>{t("grand_total")}</b></Table.Summary.Cell>
                                <Table.Summary.Cell index={4}><b>{formatNumber(filteredGrandTotalIQD)}</b></Table.Summary.Cell>
                                <Table.Summary.Cell index={5}><b>{formatNumber(filteredGrandTotalUSD)}</b></Table.Summary.Cell>
                            </Table.Summary.Row>
                        )}
                    />
                    <Row gutter={16} style={{ marginTop: 24 }}>
                        <Col span={12}><b>{`${t("totalAmount")} IQD`}</b>: {formatNumber(filteredGrandTotalIQD)}</Col>
                        <Col span={12}><b>{`${t("totalAmount")} USD`}</b>: {formatNumber(filteredGrandTotalUSD)}</Col>
                    </Row>
                </>
            )}
        </Card>
    );
};

export default ProductStockValueReportPage;
